import {
  faArrowDownToArc,
  faArrowRightLong,
  faQuestionCircle,
} from '@fortawesome/pro-regular-svg-icons'
import { Text, Box, Tooltip } from '@mantine/core'
import React, { useState } from 'react'

import {
  useMutationUpdateBooking,
  useQueryGetBookingById,
  useQueryGetAllBookingProducts,
} from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreBookingProduct, type ParameterID } from '@flecto/types'
import {
  CardList,
  Icon,
  Modal,
  TextCurrency,
  useNotifications,
} from '@flecto/ui'

import { SectionCustomerNotificationEmail } from '../../../booking-customer'
import { ButtonBookingFlectoLinkCopy } from '../../../bookings'

export type ModalBookingReturnConfirmationProps = {
  bookingId?: ParameterID
  bookingProductsToReturn?: CoreBookingProduct[]
  onSuccess?: () => void
}

export function ModalBookingReturnConfirmation({
  bookingId,
  bookingProductsToReturn,
  onSuccess,
}: ModalBookingReturnConfirmationProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_pickup_return' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })

  const { showNotification } = useNotifications()

  const [notifyCustomer, setNotifyCustomer] = useState(false)

  const { data: booking } = useQueryGetBookingById({ bookingId })
  const { data: bookingItems } = useQueryGetAllBookingProducts({ bookingId })

  const { mutateAsync: updateBooking, isLoading: isUpdating } =
    useMutationUpdateBooking()

  const totalItemsToReturn = bookingProductsToReturn?.reduce(
    (acc, bi) => acc + Number(bi.local_new_returned_quantity),
    0
  )

  const totalKeptDeposit = bookingProductsToReturn?.reduce(
    (acc, bi) => acc + Number(bi.kept_deposit_cents),
    0
  )

  const handleSubmit = async () => {
    if (!bookingId) {
      return
    }

    try {
      const bookingItemsToUpdate: Pick<
        CoreBookingProduct,
        'id' | 'returned_quantity' | 'kept_deposit_cents'
      >[] = []

      for (const item of bookingItems ?? []) {
        const pickupBookingItem = bookingProductsToReturn?.find(
          (bi) => bi.id === item.id
        )

        bookingItemsToUpdate.push({
          id: item.id,
          returned_quantity: pickupBookingItem?.local_new_returned_quantity ?? 0,
          kept_deposit_cents: pickupBookingItem?.kept_deposit_cents ?? 0,
        })
      }

      await updateBooking({
        bookingId,
        options: {
          send_email: notifyCustomer,
        },
        booking: {
          booking_items_attributes: bookingItemsToUpdate,
        },
      })

      onSuccess?.()
    } catch {
      showNotification({
        type: 'error',
        message: t('notification_return_confirmation_error'),
      })
    }
  }

  return (
    <Modal size='md'>
      <Modal.Icon icon={faArrowDownToArc} color='green' />
      <Modal.Title>{t('modal_return_confirmation_title')}</Modal.Title>
      <Modal.Content>
        <CardList>
          <CardList.Item>
            <CardList.ItemLabel w={180}>
              {t('modal_return_confirmation_items_to_return')}
            </CardList.ItemLabel>
            <CardList.ItemValue>
              <Text color='dimmed'>
                {booking?.meta?.total_returned_quantity}
              </Text>
              <Icon icon={faArrowRightLong} />
              <Text>{totalItemsToReturn}</Text>
            </CardList.ItemValue>
          </CardList.Item>
          <CardList.Item>
            <CardList.ItemLabel w={180}>
              {t('modal_return_confirmation_deposit_kept')}
              <Tooltip
                label={t('modal_pickup_confirmation_tooltip_deposit_kept')}
              >
                <Box display='inline' ml='sm'>
                  <Icon icon={faQuestionCircle} />
                </Box>
              </Tooltip>
            </CardList.ItemLabel>
            <CardList.ItemValue>
              <TextCurrency cents={totalKeptDeposit ?? 0} lh={1} />
              <Text color='dimmed'>{t('label_from')}</Text>
              <TextCurrency
                color='dimmed'
                cents={booking?.meta?.deposit_cents ?? 0}
              />
            </CardList.ItemValue>
          </CardList.Item>
        </CardList>
        <SectionCustomerNotificationEmail
          mt='lg'
          customerId={booking?.customer?.internal_id}
          value={notifyCustomer}
          onChange={setNotifyCustomer}
        />
      </Modal.Content>
      <Modal.Close />
      <Modal.Cancel />
      <Modal.Submit onClick={handleSubmit} loading={isUpdating}>
        {tActions('confirm')}
      </Modal.Submit>
      <Modal.FooterExtra>
        <ButtonBookingFlectoLinkCopy booking={booking} />
      </Modal.FooterExtra>
    </Modal>
  )
}
